// LanguageContext — the app-level language controller. Deliberately mirrors
// ThemeBridgeProvider (ARCHITECTURE.md ADR #11): a cross-cutting UI concern lives
// in React Context, NOT Redux, so reusable components stay store-independent.
//
// It wraps the i18next instance: `setLanguage(code)` calls i18n.changeLanguage()
// (which re-renders every useTranslation() subscriber in real time — no refresh)
// and the detector caches the choice to localStorage. The provider also mirrors the
// active language onto <html lang> for a11y, and gates the first paint until i18n's
// resources are ready so the UI never flashes raw keys.
import {
  createContext, useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../components/reusable';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { i18n, ready } = useTranslation();

  // True while a language switch is in flight (i18next may fetch the target
  // locale's resources over HTTP). AppLayout shows an in-shell loader during
  // this window so the switch reads as intentional rather than a silent stall.
  const [switching, setSwitching] = useState(false);

  // Normalize to the base language code ('es-MX' → 'es'); fall back to the default.
  const language = (i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE).split('-')[0];

  const setLanguage = useCallback(
    async (code) => {
      if (!code || code === i18n.language) return;
      setSwitching(true);
      // Both locales are preloaded (i18n config), so changeLanguage() resolves
      // almost instantly — without a floor the loader would flip on/off within a
      // single tick and never actually paint. Hold it for a minimum window so the
      // switch reads as a deliberate action.
      const MIN_VISIBLE_MS = 600;
      const start = performance.now();
      try {
        await i18n.changeLanguage(code);
      } finally {
        const remaining = MIN_VISIBLE_MS - (performance.now() - start);
        if (remaining > 0) {
          await new Promise((resolve) => { setTimeout(resolve, remaining); });
        }
        setSwitching(false);
      }
    },
    [i18n],
  );

  // Keep the document language attribute in sync (screen readers, hyphenation).
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      switching,
      languages: SUPPORTED_LANGUAGES,
    }),
    [language, setLanguage, switching],
  );

  // First paint: hold until translations load so we never render raw keys. (The
  // language-switch loader is shown in-shell by AppLayout via `switching`, so the
  // header/footer — including the language selector — stay visible during a switch.)
  if (!ready) {
    return <Spinner size="lg" label="Loading" />;
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

LanguageProvider.propTypes = {
  children: PropTypes.node,
};

export { LanguageContext };
export default LanguageProvider;
